import React, {
  memo,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { useWindowSize } from "react-use";
import { NavLink, useLocation } from "react-router-dom";
import classNames from "classnames";
import { NavHashLink } from "react-router-hash-link";
import Collapse from "../../base/Collapse";
import ThemeContext from "../../../Contexts/themeContext";
import useDarkMode from "../../../Hooks/useDarkMode";
import List from "./List";

// Import needed icons from lucide-react
import { REACT_APP_MOBILE_BREAKPOINT_SIZE } from "../../../Utils/Constant";
import Icon from "../../base/Icon";

const Item = ({
  children,
  to,
  title,
  icon,
  id,
  parentId,
  rootId,
  isHorizontal,
  notification,
  isMore,
  hide,
  ...props
}) => {
  const { darkModeStatus } = useDarkMode();
  const { width } = useWindowSize();
  const { setAsideStatus, setLeftMenuStatus, setRightMenuStatus } =
    useContext(ThemeContext);

  const ACTIVE = props.activeItem === id;

  const handleClick = () => {
    if (typeof props.setActiveItem !== "undefined") {
      ACTIVE ? props.setActiveItem(null) : props.setActiveItem(id);
    }
  };

  const linkHandleClick = () => {
    if (width < Number(REACT_APP_MOBILE_BREAKPOINT_SIZE)) setAsideStatus(false);
    setLeftMenuStatus(false);
    setRightMenuStatus(false);
  };

  const ANCHOR_LINK_PATTERN = /^#/i;
  const location = useLocation();

  const here =
    typeof to === "string" && to !== "/" && location.pathname.includes(to);
  const match = to !== "/" && location.pathname === to;

  const LINK_CLASS = classNames("navigation-link", "navigation-link-pill", {
    collapsed: !!children && !isHorizontal,
    active: isHorizontal ? match : here,
  });

  const INNER = (
    <>
      <span className="navigation-link-info">
        {icon && <Icon iconName={icon} className="navigation-icon" width="20" height="20" />}
        {title && <span className="navigation-text">{title}</span>}
      </span>
      {(!!children || !!notification) && (
        <span className="navigation-link-extra">
          {!!notification && (
            <Icon
              iconName={"CircleDashed"}
              className={classNames(
                "navigation-notification",
                {
                  [`text-${notification}`]: typeof notification === "string",
                  "text-danger": typeof notification !== "string",
                },
                "animate__animated animate__heartBeat animate__infinite animate__slower"
              )}
            />
          )}
          {!!children && (
            <Icon iconName={"ChevronRight"} className="navigation-arrow" width="18" height="18"/>
          )}
        </span>
      )}
    </>
  );

  const WITHOUT_CHILD =
    !children &&
    !hide &&
    ((typeof to === "string" && ANCHOR_LINK_PATTERN.test(to) && (
      <NavHashLink className={LINK_CLASS} to={to} onClick={linkHandleClick}>
        {INNER}
      </NavHashLink>
    )) || (
      <NavLink
        end
        className={({ isActive }) =>
          classNames(LINK_CLASS, isActive ? "active" : "")
        }
        to={`../${to}`}
        onClick={linkHandleClick}
      >
        {INNER}
      </NavLink>
    ));

  // Dropdown positioning logic (manual)
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownListRef = useRef(null);

  const [dropdownStatus, setDropdownStatus] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const dropdownButtonHandleClick = () => {
    setDropdownStatus(!dropdownStatus);
  };

  const closeMenu = useCallback(() => {
    setDropdownStatus(false);
  }, []);

  useEffect(() => {
    if (
      dropdownStatus &&
      dropdownButtonRef.current &&
      dropdownListRef.current
    ) {
      const buttonRect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "absolute",
        top: `${buttonRect.bottom + window.scrollY}px`,
        left: `${buttonRect.left + window.scrollX}px`,
        minWidth: `${buttonRect.width}px`,
        zIndex: 1000,
      });
    }
  }, [dropdownStatus]);

  if (children) {
    if (isHorizontal) {
      return (
        <li
          ref={dropdownRef}
          className={classNames("navigation-item", "dropdown", {
            "navigation-item-more": isMore,
          })}
        >
          <span
            ref={dropdownButtonRef}
            id={`${rootId}__${id}--link`}
            className={LINK_CLASS}
            aria-expanded={dropdownStatus}
            aria-controls={`${rootId}__${id}`}
            role="button"
            tabIndex={-1}
            onClick={dropdownButtonHandleClick}
            onKeyDown={dropdownButtonHandleClick}
          >
            {INNER}
          </span>

          {dropdownStatus && (
            <List
              ref={dropdownListRef}
              style={dropdownStyle}
              id={`${rootId}__${id}`}
              className={classNames(
                "dropdown-menu",
                { "dropdown-menu-dark": darkModeStatus },
                "show"
              )}
              ariaLabelledby={`${rootId}__${id}--link`}
              rootId={rootId}
              parentId={`${rootId}__${parentId}`}
              onMouseLeave={closeMenu}
            >
              {children}
            </List>
          )}
        </li>
      );
    }

    return (
      <li className="navigation-item">
        <span
          id={`${rootId}__${id}--link`}
          className={LINK_CLASS}
          aria-expanded={ACTIVE}
          aria-controls={`${rootId}__${id}`}
          role="button"
          tabIndex={-1}
          onClick={handleClick}
          onKeyDown={handleClick}
        >
          {INNER}
        </span>
        <Collapse isOpen={ACTIVE} isChildClone>
          <List
            id={`${rootId}__${id}`}
            ariaLabelledby={`${rootId}__${id}--link`}
            rootId={rootId}
            parentId={`${rootId}__${parentId}`}
            onMouseLeave={closeMenu}
          >
            {children}
          </List>
        </Collapse>
      </li>
    );
  }

  return <li className="navigation-item">{WITHOUT_CHILD}</li>;
};

export default memo(Item);
